import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Alert,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  Description as DocumentIcon,
  School as TrainingIcon,
  Checklist as ChecklistIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import { formatDateOnly } from '../utils/dateFormat';
import DocumentSelectionModal from '../components/DocumentSelectionModal';

interface TrainingMatrixCell {
  user_id: number;
  user_name: string;
  training_id: number;
  training_title: string;
  status: string;
  relevant_date?: string | null;
  training_version?: string | null;
}

interface DocumentMatrixCell {
  user_id: number;
  user_name: string;
  document_id: number;
  document_title: string;
  status: string;
  relevant_date?: string | null;
}

interface ChecklistHistoryItem {
  checklist_id: number;
  checklist_title: string;
  user_id: number;
  user_name: string;
  completed_at: string;
  status: string;
}

interface AuditDashboardData {
  company_name: string;
  report_date: string;
  training_matrix: {
    users: Array<{ id: number; name: string; email: string }>;
    trainings: Array<{ id: number; title: string }>;
    matrix: TrainingMatrixCell[];
  };
  document_matrix: {
    users: Array<{ id: number; name: string; email: string }>;
    documents: Array<{ id: number; title: string }>;
    matrix: DocumentMatrixCell[];
  };
  checklist_history: {
    items: ChecklistHistoryItem[];
  };
}

interface AcknowledgmentExportDocument {
  id: number;
  title: string;
  version: string;
  is_current_version: boolean;
  revision_date: string;
  acknowledged_count: number;
}

const EMPTY_ACK_EXPORT_MESSAGE = 'No acknowledged document revisions are available for export.';

async function messageFromApiError(err: any, fallback: string): Promise<string> {
  const data = err?.response?.data;
  if (data && typeof data.text === 'function') {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.error) return parsed.error;
    } catch {
      // Blob bodies that are not JSON fall through to the generic message.
    }
  }
  return data?.error || fallback;
}

const AuditDashboard: React.FC = () => {
  const [data, setData] = useState<AuditDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingAckZip, setGeneratingAckZip] = useState(false);
  const [generatingAckBundle, setGeneratingAckBundle] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultDocumentIds, setDefaultDocumentIds] = useState<number[]>([]);
  const [ackBundleModalOpen, setAckBundleModalOpen] = useState(false);
  const [ackExportDocuments, setAckExportDocuments] = useState<AcknowledgmentExportDocument[]>([]);
  const [selectedAckDocumentId, setSelectedAckDocumentId] = useState('');

  useEffect(() => {
    fetchDashboardData();
    fetchDefaultDocumentSettings();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/audit/dashboard/');
      setData(response.data);
    } catch (err: any) {
      console.error('Error fetching audit dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load audit dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultDocumentSettings = async () => {
    try {
      const response = await api.get('/audit/default_documents/');
      setDefaultDocumentIds(response.data.document_ids || []);
    } catch (err: any) {
      console.error('Error fetching default document settings:', err);
      // If endpoint doesn't exist or fails, default to empty array
      setDefaultDocumentIds([]);
    }
  };

  const handleGeneratePdfClick = () => {
    // Open modal first
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleGeneratePdf = async (documentIds: number[]) => {
    try {
      setModalOpen(false);
      setGeneratingPdf(true);
      setError(null);
      
      // Build query parameter for document IDs
      const params: any = {};
      if (documentIds.length > 0) {
        params.document_ids = documentIds.join(',');
      }
      
      const response = await api.get('/audit/report_pdf/', {
        params,
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_report_${data?.company_name || 'report'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error generating PDF:', err);
      setError(err.response?.data?.error || 'Failed to generate PDF report');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const downloadBlob = (blobData: BlobPart, type: string, filename: string) => {
    const blob = new Blob([blobData], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadAcknowledgmentZip = async () => {
    try {
      setGeneratingAckZip(true);
      setActionNotice(null);

      const documentsResponse = await api.get('/audit/acknowledgment_export_documents/');
      const documents = documentsResponse.data || [];
      if (documents.length === 0) {
        setActionNotice(EMPTY_ACK_EXPORT_MESSAGE);
        return;
      }

      const response = await api.get('/audit/acknowledgment_packages_zip/', {
        responseType: 'blob',
      });
      downloadBlob(
        response.data,
        'application/zip',
        `acknowledgment_packages_${data?.company_name || 'company'}_${new Date().toISOString().split('T')[0]}.zip`
      );
    } catch (err: any) {
      console.error('Error downloading acknowledgment packages:', err);
      setActionNotice(await messageFromApiError(err, 'Failed to download acknowledgment packages'));
    } finally {
      setGeneratingAckZip(false);
    }
  };

  const handleOpenAckBundleModal = async () => {
    try {
      setGeneratingAckBundle(true);
      setError(null);

      const response = await api.get('/audit/acknowledgment_export_documents/');
      const documents = response.data || [];
      setAckExportDocuments(documents);
      setSelectedAckDocumentId(documents[0]?.id?.toString() || '');
      setAckBundleModalOpen(true);
    } catch (err: any) {
      console.error('Error loading acknowledgment export documents:', err);
      setError(err.response?.data?.error || 'Failed to load acknowledgment export documents');
    } finally {
      setGeneratingAckBundle(false);
    }
  };

  const handleDownloadAckBundle = async () => {
    if (!selectedAckDocumentId) {
      return;
    }

    try {
      setGeneratingAckBundle(true);
      setError(null);

      const selectedDocument = ackExportDocuments.find((doc) => doc.id.toString() === selectedAckDocumentId);
      const response = await api.get('/audit/document_acknowledgments_pdf/', {
        params: { document_id: selectedAckDocumentId },
        responseType: 'blob',
      });
      downloadBlob(
        response.data,
        'application/pdf',
        `${selectedDocument?.title || 'document'}_${selectedDocument?.version || 'version'}_all_acknowledgments_${new Date().toISOString().split('T')[0]}.pdf`
      );
      setAckBundleModalOpen(false);
    } catch (err: any) {
      console.error('Error downloading document acknowledgments PDF:', err);
      setError(err.response?.data?.error || 'Failed to download document acknowledgments PDF');
    } finally {
      setGeneratingAckBundle(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } = {
      'Not Assigned': 'default',
      'Assigned': 'info',
      'In Progress': 'info',
      'Completed': 'success',
      'Past Due': 'error',
      'Overdue': 'error',
    };
    return colors[status] || 'default';
  };

  const buildTrainingMatrixData = (
    users: Array<{ id: number; name: string }>,
    trainings: Array<{ id: number; title: string }>,
    matrix: TrainingMatrixCell[]
  ) => {
    const data: {
      [key: string]: {
        [key: string]: { status: string; date?: string | null; version?: string | null };
      };
    } = {};
    users.forEach((user) => {
      data[user.id.toString()] = {};
      trainings.forEach((training) => {
        const cell = matrix.find(
          (m) => m.user_id === user.id && m.training_id === training.id
        );
        data[user.id.toString()][training.id.toString()] = {
          status: cell?.status || 'Not Assigned',
          date: cell?.relevant_date || null,
          version: cell?.training_version || null,
        };
      });
    });
    return data;
  };

  const buildDocumentMatrixData = (
    users: Array<{ id: number; name: string }>,
    documents: Array<{ id: number; title: string }>,
    matrix: DocumentMatrixCell[]
  ) => {
    const data: { [key: string]: { [key: string]: { status: string; date?: string | null } } } = {};
    users.forEach((user) => {
      data[user.id.toString()] = {};
      documents.forEach((document) => {
        const cell = matrix.find(
          (m) => m.user_id === user.id && m.document_id === document.id
        );
        data[user.id.toString()][document.id.toString()] = {
          status: cell?.status || 'Not Assigned',
          date: cell?.relevant_date || null,
        };
      });
    });
    return data;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const trainingMatrixData = buildTrainingMatrixData(
    data.training_matrix.users,
    data.training_matrix.trainings,
    data.training_matrix.matrix
  );

  const documentMatrixData = buildDocumentMatrixData(
    data.document_matrix.users,
    data.document_matrix.documents,
    data.document_matrix.matrix
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Audit Dashboard
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadAcknowledgmentZip}
            disabled={generatingAckZip}
          >
            {generatingAckZip ? 'Downloading ZIP...' : 'Acknowledgment Packages ZIP'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleOpenAckBundleModal}
            disabled={generatingAckBundle}
          >
            {generatingAckBundle ? 'Loading...' : 'Document + All Acknowledgments'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleGeneratePdfClick}
            disabled={generatingPdf}
          >
            {generatingPdf ? 'Generating PDF...' : 'Generate PDF Report'}
          </Button>
        </Box>
      </Box>

      {actionNotice && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionNotice(null)}>
          {actionNotice}
        </Alert>
      )}

      <DocumentSelectionModal
        open={modalOpen}
        onClose={handleModalClose}
        onGenerate={handleGeneratePdf}
        defaultDocumentIds={defaultDocumentIds}
        generatingPdf={generatingPdf}
      />

      <Dialog
        open={ackBundleModalOpen}
        onClose={() => setAckBundleModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Download Document + Acknowledgments</DialogTitle>
        <DialogContent>
          {ackExportDocuments.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No acknowledged document revisions are available for export.
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="ack-document-select-label">Document revision</InputLabel>
              <Select
                labelId="ack-document-select-label"
                value={selectedAckDocumentId}
                label="Document revision"
                onChange={(event) => setSelectedAckDocumentId(event.target.value)}
              >
                {ackExportDocuments.map((document) => (
                  <MenuItem key={document.id} value={document.id.toString()}>
                    {document.title} ({document.version}) - {document.acknowledged_count} acknowledgments
                    {document.is_current_version ? ' - current' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAckBundleModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadAckBundle}
            disabled={!selectedAckDocumentId || generatingAckBundle}
          >
            {generatingAckBundle ? 'Downloading...' : 'Download PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        {/* Training Matrix */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<TrainingIcon />}
              title="Training Matrix"
              subheader={`${data.training_matrix.users.length} users × ${data.training_matrix.trainings.length} trainings`}
            />
            <CardContent>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>User</strong></TableCell>
                      {data.training_matrix.trainings.map((training) => (
                        <TableCell key={training.id} align="center">
                          {training.title.length > 30
                            ? `${training.title.substring(0, 30)}...`
                            : training.title}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.training_matrix.users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell><strong>{user.name}</strong></TableCell>
                        {data.training_matrix.trainings.map((training) => {
                          const cellData = trainingMatrixData[user.id.toString()]?.[training.id.toString()] || {
                            status: 'Not Assigned',
                            date: null,
                            version: null,
                          };
                          const { status, date, version } = cellData;
                          const formatDate = (dateStr: string | null | undefined) =>
                            formatDateOnly(
                              dateStr,
                              '',
                              { month: '2-digit', day: '2-digit', year: '2-digit' },
                              'en-US'
                            );
                          return (
                            <TableCell key={training.id} align="center">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  label={status}
                                  color={getStatusColor(status)}
                                  size="small"
                                />
                                {date && (
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(date)}
                                  </Typography>
                                )}
                                {version && (
                                  <Typography variant="caption" color="text.secondary">
                                    {version}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Document Matrix */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<DocumentIcon />}
              title="Document Matrix"
              subheader={`${data.document_matrix.users.length} users × ${data.document_matrix.documents.length} documents`}
            />
            <CardContent>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>User</strong></TableCell>
                      {data.document_matrix.documents.map((document) => (
                        <TableCell key={document.id} align="center">
                          {document.title.length > 30
                            ? `${document.title.substring(0, 30)}...`
                            : document.title}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.document_matrix.users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell><strong>{user.name}</strong></TableCell>
                        {data.document_matrix.documents.map((document) => {
                          const cellData = documentMatrixData[user.id.toString()]?.[document.id.toString()] || { status: 'Not Assigned', date: null };
                          const { status, date } = cellData;
                          const formatDate = (dateStr: string | null | undefined) =>
                            formatDateOnly(
                              dateStr,
                              '',
                              { month: '2-digit', day: '2-digit', year: '2-digit' },
                              'en-US'
                            );
                          return (
                            <TableCell key={document.id} align="center">
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  label={status}
                                  color={getStatusColor(status)}
                                  size="small"
                                />
                                {date && (
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(date)}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Checklist History */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<ChecklistIcon />}
              title="Checklist Completion History"
              subheader="Past 12 Months"
            />
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Checklist</strong></TableCell>
                      <TableCell><strong>User</strong></TableCell>
                      <TableCell><strong>Completed At</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.checklist_history.items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No checklist completions found in the past 12 months.
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.checklist_history.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.checklist_title}</TableCell>
                          <TableCell>{item.user_name}</TableCell>
                          <TableCell>
                            {new Date(item.completed_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                              color={item.status === 'completed' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AuditDashboard;

